import { FC, useState } from "react";
import { Modal, Table, Tag, Typography, Spin, Button, Image } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import { useGetPayoutItems } from "hooks/useFactory";
import { PayoutItem } from "services/interface";
import { formatDateAndTime } from "utils/convertData";
import { numberFormatePrice } from "utils/formateNumber";
import { exportPayoutDetailToExcel } from "services/factoryAPI";
import ModalUploadReceipt from "./ModalUploadReceipt";
import ReceiptFiles, { getReceiptUrls } from "./ReceiptFiles";

interface ModalPayoutDetailProps {
  payoutId: string | null;
  /** บัญชีรับเงินของรายการ (จากแถวในตาราง) — ใช้ตรวจประเภทบัญชีเพื่อแสดงแบบฟอร์มใบรับเงิน */
  bankFactoryId?: string | null;
  onClose: () => void;
}

const PAYMENT_TYPE_LABEL: Record<string, string> = {
  bank_transfer: "โอนเงินผ่านธนาคาร",
  cash: "เงินสด",
  cheque: "เช็ค",
};

const ModalPayoutDetail: FC<ModalPayoutDetailProps> = ({
  payoutId,
  bankFactoryId,
  onClose,
}) => {
  const { data, isLoading, refetch } = useGetPayoutItems(payoutId);
  const detail = data?.data;
  const [isExporting, setIsExporting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const receiptUrls = getReceiptUrls(detail);
  const isPaidNoReceipt =
    detail?.payout_status === "paid" && receiptUrls.length === 0;

  const handleExport = async () => {
    if (!detail) return;
    setIsExporting(true);
    try {
      await exportPayoutDetailToExcel(detail);
    } finally {
      setIsExporting(false);
    }
  };

  const handleUploadSuccess = () => {
    void refetch();
  };

  const columns: ColumnsType<PayoutItem> = [
    {
      title: "หมายเลขคำสั่งซื้อ",
      dataIndex: "order_code",
      width: 160,
    },
    {
      title: "ชื่อสินค้า",
      dataIndex: "product_name",
      width: 200,
      ellipsis: true,
    },
    {
      title: "ตัวเลือกสินค้า",
      dataIndex: "sku_description",
      width: 180,
      ellipsis: true,
    },
    {
      title: "จำนวน",
      dataIndex: "item_qty",
      width: 80,
      align: "center",
    },
    {
      title: "ราคารวมที่โอนให้ผู้ประกอบการรวม vat",
      dataIndex: "total_amount",
      width: 130,
      align: "right",
      render: (val: number) => (
        <Typography.Text strong style={{ color: "#1d39c4" }}>
          {numberFormatePrice(val)}
        </Typography.Text>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="รายละเอียดรายการโอนเงิน"
        open={!!payoutId}
        onCancel={onClose}
        maskClosable={false}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {detail?.payout_status === "paid" && (
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={() => setUploadOpen(true)}
              >
                {isPaidNoReceipt ? "แนบเอกสารใบเสร็จรับเงิน" : "จัดการใบเสร็จ"}
              </Button>
            )}
            <Button
              type="default"
              onClick={handleExport}
              loading={isExporting}
              disabled={!detail}
            >
              Export Excel
            </Button>
          </div>
        }
        width={1400}
        style={{ top: 20 }}
      >
        {isLoading ? (
          <SpinWrapper>
            <Spin size="large" />
          </SpinWrapper>
        ) : (
          <>
            <HeaderRow>
              <InfoItem>
                <Typography.Text type="secondary">
                  หมายเลขรายการ
                </Typography.Text>
                <Typography.Text strong>
                  {detail?.payout_code ?? "-"}
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">
                  วันที่ทำรายการ
                </Typography.Text>
                <Typography.Text>
                  {detail?.create_date
                    ? formatDateAndTime(detail.create_date)
                    : "-"}
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">
                  วันที่โอนเงิน
                </Typography.Text>
                <Typography.Text>
                  {detail?.paid_at ? formatDateAndTime(detail.paid_at) : "-"}
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">
                  ประเภทการชำระ
                </Typography.Text>
                <Typography.Text>
                  {detail?.payment_type
                    ? (PAYMENT_TYPE_LABEL[detail.payment_type] ??
                      detail.payment_type)
                    : "-"}
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">จำนวนรายการ</Typography.Text>
                <Typography.Text>
                  {detail?.items.length ?? 0} รายการ
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">ยอดรวม</Typography.Text>
                <Typography.Text strong style={{ color: "#1d39c4" }}>
                  {numberFormatePrice(detail?.total_amount ?? 0)} บาท
                </Typography.Text>
              </InfoItem>
              <InfoItem>
                <Typography.Text type="secondary">สถานะ</Typography.Text>
                {detail?.payout_status === "pending" ? (
                  <Tag color="orange">รอโอน</Tag>
                ) : isPaidNoReceipt ? (
                  <Tag color="gold">โอนแล้วรอใบเสร็จ</Tag>
                ) : (
                  <Tag color="green">เสร็จสิ้น</Tag>
                )}
              </InfoItem>
            </HeaderRow>

            <SectionRow>
              <InfoBlock>
                <SectionTitle>บัญชีรับเงิน</SectionTitle>
                <InfoGrid>
                  <InfoItem>
                    <Typography.Text type="secondary">ธนาคาร</Typography.Text>
                    <Typography.Text>
                      {detail?.bank_name ?? "-"}
                    </Typography.Text>
                  </InfoItem>
                  <InfoItem>
                    <Typography.Text type="secondary">เลขบัญชี</Typography.Text>
                    <Typography.Text>
                      {detail?.recipient_account ?? "-"}
                    </Typography.Text>
                  </InfoItem>
                  <InfoItem>
                    <Typography.Text type="secondary">
                      ชื่อบัญชี
                    </Typography.Text>
                    <Typography.Text>
                      {detail?.receiver_name ?? "-"}
                    </Typography.Text>
                  </InfoItem>
                  {detail?.note && (
                    <InfoItem>
                      <Typography.Text type="secondary">
                        หมายเหตุ
                      </Typography.Text>
                      <Typography.Text>{detail.note}</Typography.Text>
                    </InfoItem>
                  )}

                  <EvidenceBlock>
                    <InfoItem>
                      <Typography.Text type="secondary">
                        สลิปการโอน
                      </Typography.Text>
                      {detail?.payment_evidence_url ? (
                        <Image
                          src={detail.payment_evidence_url}
                          width={80}
                          height={80}
                          style={{ objectFit: "contain", borderRadius: 4 }}
                        />
                      ) : (
                        <Typography.Text>-</Typography.Text>
                      )}
                    </InfoItem>
                  </EvidenceBlock>
                </InfoGrid>
              </InfoBlock>
            </SectionRow>

            <SectionRow>
              <InfoBlock>
                <SectionTitle>ใบเสร็จรับเงิน</SectionTitle>
                  <EvidenceBlock style={{ marginTop: 10 }}>
                    <InfoItem>
                      {/* <Typography.Text type="secondary">
                    ใบเสร็จรับเงิน
                  </Typography.Text> */}
                      {receiptUrls.length ? (
                        <ReceiptFiles urls={receiptUrls} size={80} />
                      ) : (
                        <Typography.Text
                          type={isPaidNoReceipt ? "danger" : "secondary"}
                        >
                          {isPaidNoReceipt ? "ยังไม่อัพโหลด" : "-"}
                        </Typography.Text>
                      )}
                    </InfoItem>
                  </EvidenceBlock>
              </InfoBlock>
            </SectionRow>

            <Table<PayoutItem>
              key={payoutId ?? "none"}
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={detail?.items ?? []}
              scroll={{ x: 1000, y: 400 }}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              }}
            />
          </>
        )}
      </Modal>

      <ModalUploadReceipt
        open={uploadOpen}
        payoutId={payoutId}
        existingUrls={detail?.file_list ?? []}
        bankFactoryId={detail?.bank_factory_id ?? bankFactoryId}
        onClose={() => setUploadOpen(false)}
        onSuccess={(_id, _fileList) => {
          setUploadOpen(false);
          handleUploadSuccess();
        }}
      />
    </>
  );
};

export default ModalPayoutDetail;

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px 32px;
  padding: 12px 0 16px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
`;

const SectionRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 12px 0 16px 0;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const InfoBlock = styled.div`
  flex: 1;
  min-width: 280px;
`;

const SectionTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #595959;
  margin: 0 0 10px 0;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
`;

const EvidenceBlock = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
